import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { getAssetUrl } from '../lib/assetUrl';
import { devLog, devWarn } from '../lib/dev-logger';
import { useWorkspaceStore } from '../stores/workspace.store';
import { useAuthStore } from '../stores/auth.store';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { PlanSelectionModal } from '../components/PlanSelectionModal';
import { WorkspaceThumbnail } from '../components/WorkspaceThumbnail';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

export const Route = createFileRoute('/workspaces')({
  component: WorkspacesPage,
});

function WorkspacesPage() {
  const navigate = useNavigate();
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const queryClient = useQueryClient();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const { confirm } = useConfirmDialog();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const workspacesScrollRef = useRef<HTMLDivElement>(null);
  
  const setAuth = useAuthStore((state) => state.setAuth);
  const currentUser = useAuthStore((state) => state.user);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPlanSelectionModal, setShowPlanSelectionModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectingWorkspaceId, setSelectingWorkspaceId] = useState<string | null>(null);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const dragStateRef = useRef({ isDragging: false, startX: 0, scrollLeft: 0 });
  /** Evita fechar o modal ao soltar o mouse no overlay após arrastar a partir do conteúdo */
  const createModalMouseDownOnBackdropRef = useRef(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    description: '',
    fullName: '',
    phone: '',
    address: '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  // Form states for create
  const [createName, setCreateName] = useState('');
  const [createSlug, setCreateSlug] = useState('');
  const [createImagePreview, setCreateImagePreview] = useState<string | null>(null);
  const [createImageFile, setCreateImageFile] = useState<File | null>(null);
  const [createImageKey, setCreateImageKey] = useState<string | null>(null);

  // Form states for edit
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editImageFile, setEditImageFile] = useState<File | null>(null);
  const [editImageKey, setEditImageKey] = useState<string | null>(null);

  const resetCreateWorkspaceForm = useCallback(() => {
    setCreateName('');
    setCreateSlug('');
    setCreateImagePreview(null);
    setCreateImageFile(null);
    setCreateImageKey(null);
  }, []);

  const closeCreateWorkspaceModal = useCallback(() => {
    setShowCreateModal(false);
    resetCreateWorkspaceForm();
  }, [resetCreateWorkspaceForm]);

  const handleCreateModalBackdropMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      createModalMouseDownOnBackdropRef.current = e.target === e.currentTarget;
    },
    [],
  );

  const handleCreateModalBackdropMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget && createModalMouseDownOnBackdropRef.current) {
        closeCreateWorkspaceModal();
      }
      createModalMouseDownOnBackdropRef.current = false;
    },
    [closeCreateWorkspaceModal],
  );

  const closeProfileEditModal = useCallback(() => {
    setShowProfileEditModal(false);
    setAvatarFile(null);
    setAvatarPreview(null);
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  }, []);

  // Fetch user profile data
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await api.get('/auth/me');
      // Handle response format: { data: {...} } from TransformInterceptor
      return response.data?.data || response.data;
    },
  });

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: async () => {
      const response = await api.get('/workspaces');
      const payload = response.data?.data;
      if (Array.isArray(payload)) return payload;
      return payload?.items || [];
    },
  });

  // Fetch pending invites
  const { data: pendingInvites, isLoading: isLoadingInvites } = useQuery({
    queryKey: ['pendingInvites'],
    queryFn: async () => {
      const response = await api.get('/invites/my');
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
  });

  // Fetch banners
  const { data: bannersData } = useQuery({
    queryKey: ['banners'],
    queryFn: async () => {
      try {
        const response = await api.get('/workspaces/banners/current');
        return response.data?.data || response.data;
      } catch (error) {
        return null;
      }
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: {
      name?: string;
      description?: string | null;
      fullName?: string | null;
      phone?: string | null;
      address?: string | null;
    }) => {
      // Convert empty strings to null
      const payload = {
        name: data.name?.trim(),
        description: data.description?.trim() || null,
        fullName: data.fullName?.trim() || null,
        phone: data.phone?.trim() || null,
        address: data.address?.trim() || null,
      };
      const response = await api.put('/users/me', payload);
      // Handle both response formats: { data: {...} } or direct {...}
      return response.data?.data || response.data;
    },
    onSuccess: async (data) => {
      // Invalidate and refetch to ensure UI is updated
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      queryClient.setQueryData(['userProfile'], data);
      
      // Atualizar auth store com os dados atualizados
      try {
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          useAuthStore.getState().setAuth(
            {
              ...currentUser,
              name: data.name || currentUser.name,
              avatarUrl: data.avatarUrl || currentUser.avatarUrl,
            },
            useAuthStore.getState().accessToken || '',
            useAuthStore.getState().refreshToken || ''
          );
        }
      } catch (error) {
        console.error('Erro ao atualizar auth store:', error);
      }
      
      // Invalidar queries que podem exibir o avatar
      queryClient.invalidateQueries({ queryKey: ['members'] });
    },
    onError: (error) => {
      console.error('Erro ao atualizar perfil:', error);
      alert('Erro ao salvar perfil. Por favor, tente novamente.');
    },
  });

  // Accept invite mutation
  const acceptInviteMutation = useMutation({
    mutationFn: async ({ token }: { token: string; workspaceId: string }) => {
      const response = await api.post(`/invites/${token}/accept`);
      return response.data;
    },
    onSuccess: (_data, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvites'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    },
  });

  // Reject invite mutation
  const rejectInviteMutation = useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post(`/invites/${token}/reject`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvites'] });
    },
  });

  // Upload banner mutation
  const uploadBannerMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await api.post('/users/upload-banner', formData);
      return uploadResponse.data.data.key;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await api.post('/users/upload-avatar', formData);
      return uploadResponse.data.data;
    },
    onSuccess: async (data) => {
      // Atualizar o userProfile query
      await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      
      // Buscar o perfil atualizado e atualizar o auth store
      try {
        const profileResponse = await api.get('/auth/me');
        const updatedProfile = profileResponse.data?.data || profileResponse.data;
        if (updatedProfile) {
          // Atualizar o auth store com os dados atualizados
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            useAuthStore.getState().setAuth(
              {
                ...currentUser,
                avatarUrl: updatedProfile.avatarUrl || currentUser.avatarUrl,
              },
              useAuthStore.getState().accessToken || '',
              useAuthStore.getState().refreshToken || ''
            );
          }
        }
      } catch (error) {
        console.error('Erro ao atualizar auth store:', error);
      }
      
      // Invalidar queries que podem exibir o avatar
      queryClient.invalidateQueries({ queryKey: ['members'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      
      setAvatarFile(null);
      setAvatarPreview(null);
    },
  });

  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      try {
        // Upload via backend
        const formData = new FormData();
        formData.append('file', file);

        // Don't set Content-Type header - let browser set it with boundary
        const uploadResponse = await api.post('/workspaces/upload-image', formData);

        const { key } = uploadResponse.data.data;
        return key;
      } catch (error) {
        console.error('Upload error:', error);
        throw error;
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; slug: string; imageKey?: string | null }) => {
      const response = await api.post('/workspaces', {
        name: data.name,
        slug: data.slug,
        imageUrl: data.imageKey || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      closeCreateWorkspaceModal();
    },
    onError: (error: any) => {
      // Check if it's a quota limit error
      if (error.response?.status === 403 && error.response?.data?.availablePlans) {
        // Close the create modal and open plan selection
        closeCreateWorkspaceModal();
        setShowPlanSelectionModal(true);
      } else {
        const isSlugConflict = error.response?.status === 409;
        confirm({
          title: isSlugConflict ? 'Identificador já em uso' : 'Erro ao criar workspace',
          description:
            isSlugConflict
              ? 'Já existe um workspace com esse identificador. Por favor, escolha um diferente.'
              : (error.response?.data?.message || 'Ocorreu um erro ao criar o workspace. Tente novamente.'),
          confirmLabel: 'Entendido',
          cancelLabel: 'Fechar',
          variant: 'warning',
        });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, imageKey }: { id: string; name: string; imageKey?: string | null }) => {
      const response = await api.put(`/workspaces/${id}`, {
        name,
        imageUrl: imageKey || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setEditingId(null);
      setEditName('');
      setEditImagePreview(null);
      setEditImageFile(null);
      setEditImageKey(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/workspaces/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setDeleteConfirmId(null);
    },
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (workspaceId: string) => {
      const response = await api.put(`/workspaces/${workspaceId}/favorite`);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });

  const handleSelectWorkspace = async (workspace: any) => {
    if (selectingWorkspaceId) return;
    devLog('workspace-select', 'Usuário clicou em workspace', {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
    });
    setSelectingWorkspaceId(workspace.id);

    try {
      const response = await api.get<{ data: string[] }>('/permissions/me', {
        headers: {
          'X-Workspace-Id': workspace.id,
        },
      });
      const permissions = response.data?.data;
      const permissionList = Array.isArray(permissions) ? permissions : [];
      if (!permissionList.includes('workspace.read')) {
        devWarn('workspace-select', 'workspace.read ausente ao selecionar workspace', {
          workspaceId: workspace.id,
          permissionsCount: permissionList.length,
        });
        showPermissionDenied('Ver workspace');
        return;
      }

      queryClient.setQueryData(
        ['permissions', 'me', workspace.id],
        permissionList
      );

      setWorkspace(workspace);
      devLog('workspace-select', 'Permissão validada, navegando para o workspace', {
        workspaceId: workspace.id,
      });
      navigate({ to: `/w/${workspace.id}/clients` });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403 || status === 400) {
        devWarn('workspace-select', 'Validação de permissão negada no backend', {
          workspaceId: workspace.id,
          status,
          message: error?.response?.data?.message,
        });
        showPermissionDenied('Ver workspace');
        return;
      }

      devWarn('workspace-select', 'Erro inesperado ao validar workspace', {
        workspaceId: workspace.id,
        status,
        message: error?.response?.data?.message,
      });
      alert(error?.response?.data?.message || 'Erro ao validar acesso ao workspace');
    } finally {
      setSelectingWorkspaceId(null);
    }
  };

  const handleEditClick = (e: React.MouseEvent, workspace: any) => {
    e.stopPropagation();
    setEditingId(workspace.id);
    setEditName(workspace.name);
    setEditImagePreview(workspace.imageUrl || null);
    setEditImageFile(null);
    setEditImageKey(null);
  };

  const handleCreateImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCreateImageFile(file);
      const preview = URL.createObjectURL(file);
      setCreateImagePreview(preview);
      
      // Upload image (silently fail if S3 is not available)
      try {
        const key = await uploadImageMutation.mutateAsync(file);
        setCreateImageKey(key);
      } catch (error) {
        console.warn('Error uploading image to S3. Image will be shown locally but not saved:', error);
        // Keep preview even if upload fails - user can still see it
        // Just don't set the key so it won't be saved
      }
    }
  };

  const handleEditImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditImageFile(file);
      const preview = URL.createObjectURL(file);
      setEditImagePreview(preview);
      
      // Upload image (silently fail if S3 is not available)
      try {
        const key = await uploadImageMutation.mutateAsync(file);
        setEditImageKey(key);
      } catch (error) {
        console.warn('Error uploading image to S3. Image will be shown locally but not saved:', error);
        // Keep preview even if upload fails - user can still see it
        // Just don't set the key so it won't be saved
      }
    }
  };

  const handleSaveEdit = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const ws = workspaces?.find((w: any) => w.id === id);
    if (!ws?.isOwner) {
      showPermissionDenied('Editar workspace');
      return;
    }
    if (editName.trim()) {
      updateMutation.mutate({ id, name: editName.trim(), imageKey: editImageKey });
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
    setEditName('');
    setEditImagePreview(null);
    setEditImageFile(null);
    setEditImageKey(null);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const ws = workspaces?.find((w: any) => w.id === id);
    if (!ws?.isOwner) {
      showPermissionDenied('Apagar workspace');
      return;
    }
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      deleteMutation.mutate(deleteConfirmId);
    }
  };

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadBannerMutation.mutateAsync(file);
      } catch (error) {
        console.error('Error uploading banner:', error);
      }
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await uploadAvatarMutation.mutateAsync(file);
      } catch (error) {
        console.error('Error uploading avatar:', error);
      }
    }
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createName.trim() && createSlug.trim()) {
      createMutation.mutate({
        name: createName.trim(),
        slug: createSlug.trim().toLowerCase().replace(/\s+/g, '-'),
        imageKey: createImageKey,
      });
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside any menu
      if (!target.closest('[data-menu-container]')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Drag to scroll functionality for workspaces
  useEffect(() => {
    const container = workspacesScrollRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Don't start drag if clicking on interactive elements
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('a') || target.closest('input')) {
        return;
      }
      dragStateRef.current.isDragging = true;
      dragStateRef.current.startX = e.pageX - container.offsetLeft;
      dragStateRef.current.scrollLeft = container.scrollLeft;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
      dragStateRef.current.isDragging = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = 'auto';
      }
    };

    const handleMouseUp = () => {
      dragStateRef.current.isDragging = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = 'auto';
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStateRef.current.isDragging) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - dragStateRef.current.startX) * 2; // Scroll speed multiplier
      container.scrollLeft = dragStateRef.current.scrollLeft - walk;
    };

    const handleWheel = (e: WheelEvent) => {
      // Only handle horizontal scroll (shift + wheel or horizontal wheel)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.shiftKey) {
        e.preventDefault();
        container.scrollLeft += e.deltaY || e.deltaX;
      }
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('wheel', handleWheel, { passive: false });
    container.style.cursor = 'grab';

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  // Component to fetch and display workspace members
  function WorkspaceMembers({ workspaceId }: { workspaceId: string }) {
    const { data: members, isLoading } = useQuery({
      queryKey: ['workspace-members', workspaceId],
      queryFn: async () => {
        try {
          const response = await api.get('/members', {
            headers: { 'X-Workspace-Id': workspaceId },
          });
          return response.data.data || [];
        } catch (error) {
          return [];
        }
      },
      enabled: !!workspaceId,
    });

    if (isLoading) {
      return null;
    }

    // Get active members only, limit to 4 for display
    const activeMembers = (members || []).filter((m: any) => m.status === 'active').slice(0, 4);

    if (activeMembers.length === 0) {
      return null;
    }

    return (
      <div className="flex items-center absolute bottom-2 right-4">
        {activeMembers.map((member: any, index: number) => (
          <div
            key={member.id}
            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white dark:border-[var(--bg-tertiary)] ${
              member.user?.avatarUrl ? '' : 'bg-[#06141B] dark:bg-[var(--bg-quaternary)]'
            }`}
            style={{
              marginLeft: index > 0 ? '-6px' : 0,
              zIndex: activeMembers.length - index,
            }}
            title={member.user?.name || 'Membro'}
          >
            {member.user?.avatarUrl ? (
              <img
                src={getAssetUrl(member.user.avatarUrl) || member.user.avatarUrl}
                alt={member.user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span
                className="dark:text-[var(--text-primary)]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '10px',
                  fontWeight: 400,
                  color: '#F5F6F5',
                  textTransform: 'lowercase',
                }}
              >
                {(member.user?.name || 'm').charAt(0).toLowerCase()}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (isLoading || isLoadingProfile) {
    return (
      <div className="workspaces-page p-8 text-gray-900 dark:text-[var(--text-secondary)]">Carregando...</div>
    );
  }

  // Combine userProfile (from API) with currentUser (from store) to ensure we have all data
  const user = userProfile 
    ? { ...currentUser, ...userProfile } // userProfile has priority (includes bannerUrl, avatarUrl)
    : currentUser;

  return (
    <div className="workspaces-page min-h-screen bg-gray-50 dark:bg-[var(--bg-primary)]">
      {/* Banner Section - Removido visualmente, pode ser reativado no futuro */}
      {false && (
      <div className="mt-20 px-8">
        <div className="relative w-full h-[300px] rounded-[15px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: user?.bannerUrl ? `url(${user.bannerUrl})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-20" />
          </div>

          <div className="absolute top-4 left-4 z-10">
            <h2
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '14px',
                fontWeight: 700,
                color: '#FFFFFF',
              }}
            >
              Perfil
            </h2>
          </div>

          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            onChange={handleBannerChange}
            className="hidden"
          />
          <button
            onClick={() => bannerInputRef.current?.click()}
            className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm rounded-lg p-2 transition-all"
            disabled={uploadBannerMutation.isPending}
          >
            {uploadBannerMutation.isPending ? (
              <div className="text-white text-sm">Enviando...</div>
            ) : (
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            )}
          </button>
        </div>

        <div
          className="relative -mt-[57.5px] mx-auto z-20"
          style={{
            width: 'calc(100% - 4rem)',
            maxWidth: '1200px',
            minWidth: '320px',
            height: '115px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(21px)',
            borderRadius: '15px',
            boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <div className="relative flex-shrink-0">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            {user?.avatarUrl ? (
              <img
                src={getAssetUrl(user.avatarUrl) || user.avatarUrl}
                alt={user?.name || currentUser?.name || 'User'}
                className="w-20 h-20 rounded-[10px] object-cover cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => avatarInputRef.current?.click()}
              />
            ) : (
              <div
                className="w-20 h-20 rounded-[10px] bg-indigo-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => avatarInputRef.current?.click()}
                style={{ minWidth: '80px', minHeight: '80px' }}
              >
                <span className="text-indigo-600 text-2xl font-semibold">
                  {(user?.name || currentUser?.name)?.charAt(0).toUpperCase() || (user?.email || currentUser?.email)?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
            )}
            {uploadAvatarMutation.isPending && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-[10px] flex items-center justify-center">
                <div className="text-white text-xs">Enviando...</div>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-center" style={{ paddingLeft: '1rem' }}>
            <h3
              className="text-gray-900 dark:text-[#CBD1CF]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                marginBottom: '0.25rem',
              }}
            >
              {user?.name || currentUser?.name || ''}
            </h3>
            <p
              className="dark:text-[#CBD1CF]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '14px',
                fontWeight: 400,
                color: '#718096',
              }}
            >
              {user?.email || currentUser?.email || ''}
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Three Banners Section */}
      <div className="max-w-7xl mx-auto px-8 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Banner 1 */}
          <div
            className="w-full overflow-hidden bg-[#F9FAFB] dark:bg-[var(--bg-secondary)] shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-md)]"
            style={{
              borderRadius: '15px',
              aspectRatio: '16/9',
              minHeight: '200px',
            }}
          >
            {bannersData?.banner1 ? (
              bannersData.banner1.type === 'youtube' ? (
                <iframe
                  src={bannersData.banner1.bannerUrl}
                  title="Banner 1 - YouTube"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : bannersData.banner1.link ? (
                <a
                  href={bannersData.banner1.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                >
                  <img
                    src={bannersData.banner1.bannerUrl}
                    alt="Banner 1"
                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    style={{
                      display: 'block',
                    }}
                  />
                </a>
              ) : (
                <img
                  src={bannersData.banner1.bannerUrl}
                  alt="Banner 1"
                  className="w-full h-full object-cover"
                  style={{
                    display: 'block',
                  }}
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-[var(--bg-tertiary)]">
                <span className="text-gray-400 dark:text-[var(--text-tertiary)] text-sm">Banner 1</span>
              </div>
            )}
          </div>

          {/* Banner 2 */}
          <div
            className="w-full overflow-hidden bg-[#F9FAFB] dark:bg-[var(--bg-secondary)] shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-md)]"
            style={{
              borderRadius: '15px',
              aspectRatio: '16/9',
              minHeight: '200px',
            }}
          >
            {bannersData?.banner2 ? (
              bannersData.banner2.type === 'youtube' ? (
                <iframe
                  src={bannersData.banner2.bannerUrl}
                  title="Banner 2 - YouTube"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : bannersData.banner2.link ? (
                <a
                  href={bannersData.banner2.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                >
                  <img
                    src={bannersData.banner2.bannerUrl}
                    alt="Banner 2"
                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    style={{
                      display: 'block',
                    }}
                  />
                </a>
              ) : (
                <img
                  src={bannersData.banner2.bannerUrl}
                  alt="Banner 2"
                  className="w-full h-full object-cover"
                  style={{
                    display: 'block',
                  }}
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-[var(--bg-tertiary)]">
                <span className="text-gray-400 dark:text-[var(--text-tertiary)] text-sm">Banner 2</span>
              </div>
            )}
          </div>

          {/* Banner 3 */}
          <div
            className="w-full overflow-hidden bg-[#F9FAFB] dark:bg-[var(--bg-secondary)] shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-md)]"
            style={{
              borderRadius: '15px',
              aspectRatio: '16/9',
              minHeight: '200px',
            }}
          >
            {bannersData?.banner3 ? (
              bannersData.banner3.type === 'youtube' ? (
                <iframe
                  src={bannersData.banner3.bannerUrl}
                  title="Banner 3 - YouTube"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : bannersData.banner3.link ? (
                <a
                  href={bannersData.banner3.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full h-full"
                >
                  <img
                    src={bannersData.banner3.bannerUrl}
                    alt="Banner 3"
                    className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    style={{
                      display: 'block',
                    }}
                  />
                </a>
              ) : (
                <img
                  src={bannersData.banner3.bannerUrl}
                  alt="Banner 3"
                  className="w-full h-full object-cover"
                  style={{
                    display: 'block',
                  }}
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-[var(--bg-tertiary)]">
                <span className="text-gray-400 dark:text-[var(--text-tertiary)] text-sm">Banner 3</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Workspaces List */}
      <div className="max-w-7xl mx-auto px-8 mt-16">
        <div
          className="p-6 bg-white dark:bg-[var(--bg-secondary)] shadow-[0_0_15px_rgba(0,0,0,0.05)] dark:shadow-[var(--shadow-md)]"
          style={{
            borderRadius: 15,
          }}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 
                className="text-gray-900 dark:text-[var(--text-primary)]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                }}
              >
                Workspace
              </h1>
              <p 
                className="dark:text-[var(--text-secondary)]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#06141B',
                  fontWeight: 400,
                }}
              >
                Workspaces que você faz parte
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="rounded-lg transition-colors hover:opacity-80 bg-[#06141B] dark:bg-[var(--primary)] shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-sm)] dark:hover:bg-[var(--primary-hover)]"
              style={{
                width: 35,
                height: 35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-[#CCD0CF] dark:text-[var(--text-inverse)]"
              >
                <path
                  d="M12 5V19M5 12H19"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <style>{`
            .workspaces-scroll-container::-webkit-scrollbar {
              height: 8px;
            }
            .workspaces-scroll-container::-webkit-scrollbar-track {
              background-color: #EEEEEE;
              border-radius: 4px;
            }
            .workspaces-scroll-container::-webkit-scrollbar-thumb {
              background-color: #AAAAAA;
              border-radius: 4px;
            }
            .workspaces-scroll-container::-webkit-scrollbar-thumb:hover {
              background-color: #999999;
            }
          `}</style>
          <div
            ref={workspacesScrollRef}
            className="flex gap-4 workspaces-scroll-container"
            style={{
              overflowX: 'auto',
              paddingBottom: '8px',
              maxWidth: '100%',
              cursor: 'grab',
              alignItems: 'flex-start',
            }}
          >
          {workspaces?.map((workspace: any) => (
            <div
              key={workspace.id}
              className="p-6 transition relative min-w-[280px] w-[280px] bg-[#F5F6F5] dark:bg-[var(--bg-tertiary)] shadow-[0_0_5.5px_rgba(0,0,0,0.02)] dark:shadow-[var(--shadow-sm)]"
              style={{
                borderRadius: 15,
                flexShrink: 0,
              }}
            >
              {editingId === workspace.id ? (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {/* Image upload */}
                  <div className="mb-4 flex flex-col items-center">
                    <div className="relative mb-2">
                      {editImagePreview ? (
                        <img
                          src={editImagePreview}
                          alt="Preview"
                          className="w-20 h-20 rounded-[10px] object-cover border border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]"
                        />
                      ) : (
                        <div
                          className="w-20 h-20 rounded-[10px] flex items-center justify-center bg-[#06141B] dark:bg-[var(--bg-quaternary)]"
                        >
                          <span
                            className="dark:text-[var(--text-primary)]"
                            style={{
                              fontSize: 24,
                              fontWeight: 400,
                              color: '#F5F6F5',
                              textTransform: 'lowercase',
                            }}
                          >
                            {(editName || 'w').charAt(0).toLowerCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={editFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      disabled={uploadImageMutation.isPending}
                      className="dark:text-[var(--primary)]"
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: '#06141B',
                        background: 'none',
                        border: 'none',
                        cursor: uploadImageMutation.isPending ? 'not-allowed' : 'pointer',
                        padding: 0,
                      }}
                      onMouseEnter={(e) => {
                        if (!uploadImageMutation.isPending) e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      {uploadImageMutation.isPending ? 'Enviando...' : 'Alterar imagem'}
                    </button>
                  </div>

                  <label
                    className="dark:text-[var(--text-secondary)]"
                    style={{
                      display: 'block',
                      fontSize: 12,
                      fontWeight: 500,
                      color: '#2D3748',
                      marginBottom: 4,
                    }}
                  >
                    Nome do workspace
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveEdit(e as any, workspace.id);
                      } else if (e.key === 'Escape') {
                        handleCancelEdit(e as any);
                      }
                    }}
                    className="dark:!bg-[var(--bg-input)] dark:!text-[var(--text-primary)] dark:!border-[var(--border-primary)]"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: 12,
                      color: '#2D3748',
                      border: '1px solid #E2E8F0',
                      borderRadius: 10,
                      minHeight: 40,
                      padding: '10px 14px',
                      width: '100%',
                      outline: 'none',
                      marginBottom: 16,
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      const isDark = document.documentElement.classList.contains('dark');
                      e.target.style.borderColor = isDark ? 'var(--border-focus)' : '#06141B';
                      e.target.style.boxShadow = isDark
                        ? 'var(--shadow-focus)'
                        : '0 0 0 3px rgba(6, 20, 27, 0.12)';
                    }}
                    onBlur={(e) => {
                      const isDark = document.documentElement.classList.contains('dark');
                      e.target.style.borderColor = isDark ? 'var(--border-primary)' : '#E2E8F0';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleSaveEdit(e, workspace.id)}
                      disabled={updateMutation.isPending || uploadImageMutation.isPending}
                      className="flex-1 rounded-lg border-none py-2 px-3.5 font-semibold text-xs text-white bg-[#06141B] shadow-[0_2px_6px_rgba(6,20,27,0.15)] hover:bg-[#0A1F29] hover:shadow-[0_2px_8px_rgba(6,20,27,0.2)] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:shadow-[var(--shadow-sm)] dark:hover:bg-[var(--primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex-1 rounded-lg py-2 px-3.5 font-semibold text-xs text-[#2D3748] bg-white border border-[#E2E8F0] hover:bg-[#F9FAFB] dark:bg-[var(--bg-input)] dark:text-[var(--text-primary)] dark:border-[var(--border-primary)] dark:hover:bg-[var(--bg-quaternary)] cursor-pointer"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Favorite Star and Kebab Menu Button */}
                  <div className="absolute top-2 right-2 flex items-center gap-2" data-menu-container>
                    {/* Favorite Star Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavoriteMutation.mutate(workspace.id);
                      }}
                      className="p-1 rounded hover:opacity-80 transition-all"
                      aria-label={workspace.isFavorite ? 'Desfavoritar workspace' : 'Favoritar workspace'}
                      disabled={toggleFavoriteMutation.isPending}
                    >
                      {workspace.isFavorite ? (
                        <svg
                          className="w-5 h-5 transition-transform hover:scale-110"
                          fill="#FFD700"
                          stroke="#FFD700"
                          viewBox="0 0 24 24"
                          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-gray-400 dark:text-[var(--text-tertiary)] transition-transform hover:scale-110"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                          />
                        </svg>
                      )}
                    </button>

                    {/* Kebab Menu Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(openMenuId === workspace.id ? null : workspace.id);
                      }}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-[var(--bg-quaternary)] transition-colors"
                      aria-label="Menu de opções"
                    >
                      <svg
                        className="w-5 h-5 text-gray-600 dark:text-[var(--text-secondary)]"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    {/* Dropdown Menu - Editar/Apagar apenas para owner do workspace */}
                    {openMenuId === workspace.id && (
                      <div
                        className="absolute top-8 right-0 bg-white dark:bg-[var(--bg-secondary)] border border-gray-200 dark:border-[var(--border-primary)] rounded-lg shadow-lg dark:shadow-[var(--shadow-md)] z-10 min-w-[120px] py-1"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {workspace.isOwner && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(e, workspace);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-[var(--bg-tertiary)] first:rounded-t-lg transition-colors text-[#2D3748] dark:text-[var(--text-primary)]"
                              style={{ fontSize: 12, fontWeight: 400 }}
                            >
                              Editar
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(e, workspace.id);
                                setOpenMenuId(null);
                              }}
                              className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-[var(--error-bg)] last:rounded-b-lg transition-colors text-[#DC2626] dark:text-[var(--error)]"
                              style={{ fontSize: 12, fontWeight: 400 }}
                            >
                              Apagar
                            </button>
                          </>
                        )}
                        {!workspace.isOwner && (
                          <div
                            className="px-4 py-2 text-gray-500 dark:text-[var(--text-tertiary)]"
                            style={{ fontSize: 12 }}
                          >
                            Apenas o dono pode editar ou apagar
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div
                    onClick={() => {
                      void handleSelectWorkspace(workspace);
                    }}
                    className={`cursor-pointer ${selectingWorkspaceId === workspace.id ? 'opacity-70 pointer-events-none' : ''}`}
                  >
                    {/* Avatar - Top Left */}
                    <div style={{ marginBottom: '8px' }}>
                      {workspace.imageUrl ? (
                        <img
                          src={getAssetUrl(workspace.imageUrl) || workspace.imageUrl}
                          alt={workspace.name}
                          className="object-cover"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                          }}
                        />
                      ) : (
                        <div 
                          className="flex items-center justify-center bg-[#06141B] dark:bg-[var(--bg-quaternary)]"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                          }}
                        >
                          <span
                            className="dark:text-[var(--text-primary)]"
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '14px',
                              fontWeight: 400,
                              color: '#F5F6F5',
                              textTransform: 'lowercase',
                            }}
                          >
                            {(workspace.name || 'w').charAt(0).toLowerCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {/* Title and Plan Tag */}
                    <div style={{ marginBottom: '20px' }}>
                      <h2 
                        className="dark:text-[var(--text-primary)]"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '14px',
                          fontWeight: 400,
                          color: '#06141B',
                          marginBottom: '1px',
                        }}
                      >
                        {workspace.name}
                      </h2>
                      
                      {/* Plan Tag - Always show, use default if no plan */}
                      <div>
                        <span 
                          className="dark:text-[var(--text-tertiary)]"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#9BA8AB',
                          }}
                        >
                          {workspace.plan?.name || 'Padrão'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Members - Bottom Right */}
                  <WorkspaceMembers workspaceId={workspace.id} />
                </>
              )}
            </div>
          ))}
        </div>

          {workspaces?.length === 0 && (
            <div className="text-center py-12 text-gray-500 dark:text-[var(--text-secondary)]">
              <p>Você ainda não tem workspaces.</p>
              <p className="text-sm mt-2 dark:text-[var(--text-tertiary)]">
                Crie um novo workspace para começar!
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Three Containers Section - Hidden */}
      {false && (
      <div className="max-w-7xl mx-auto px-8 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Container 1: Convites */}
          <div 
            className="bg-white dark:bg-gray-800 p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div>
              <h3 
                className="mb-2 text-gray-900 dark:text-[#CBD1CF]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '18px',
                  fontWeight: 600,
                }}
              >
                Meus convites
              </h3>
              {pendingInvites && pendingInvites.length > 0 && (
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 400,
                    marginBottom: '16px',
                  }}
                >
                  Você tem {pendingInvites.length} {pendingInvites.length === 1 ? 'convite pendente' : 'convites pendentes'}
                </p>
              )}
              <p 
                className="dark:text-[#CBD1CF]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                  fontWeight: 400,
                  marginBottom: '16px',
                  display: pendingInvites && pendingInvites.length > 0 ? 'none' : 'block',
                }}
              >
                Convites para workspaces
              </p>
            </div>
            {isLoadingInvites ? (
              <div 
                className="dark:text-[#CBD1CF]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                }}
              >
                Carregando...
              </div>
            ) : pendingInvites && pendingInvites.length > 0 ? (
              <>
                <style>{`
                  .invites-scroll-container::-webkit-scrollbar {
                    width: 8px;
                  }
                  .invites-scroll-container::-webkit-scrollbar-track {
                    background-color: #EEEEEE;
                    border-radius: 4px;
                  }
                  .invites-scroll-container::-webkit-scrollbar-thumb {
                    background-color: #AAAAAA;
                    border-radius: 4px;
                  }
                  .invites-scroll-container::-webkit-scrollbar-thumb:hover {
                    background-color: #999999;
                  }
                `}</style>
                <div 
                  className="space-y-3 invites-scroll-container"
                  style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    paddingRight: '8px',
                  }}
                >
                {pendingInvites.map((invite: any) => (
                  <div
                    key={invite.id}
                    className="rounded-lg p-3 transition-colors"
                    style={{
                      backgroundColor: '#F9FAFB',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    {/* Miniatura do workspace */}
                    <div style={{ flexShrink: 0 }}>
                      <WorkspaceThumbnail
                        imageUrl={invite.workspace?.imageUrl}
                        name={invite.workspace?.name}
                        size={32}
                        borderRadius={5}
                      />
                    </div>

                    {/* Nome do workspace e texto do convite */}
                    <div className="flex-1 min-w-0">
                      <div 
                        className="text-gray-900 dark:text-[#CBD1CF]"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '14px',
                          fontWeight: 600,
                          marginBottom: '4px',
                        }}
                      >
                        {invite.workspace?.name || 'Workspace'}
                      </div>
                      {invite.role && (
                        <div 
                          className="dark:text-[#CBD1CF]"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '10px',
                            color: '#718096',
                            fontWeight: 400,
                          }}
                        >
                          Você foi convidado como {invite.role.name}
                        </div>
                      )}
                    </div>

                    {/* Botões de ação */}
                    <div className="flex gap-2" style={{ flexShrink: 0 }}>
                      <button
                        onClick={() => acceptInviteMutation.mutate({ token: invite.token, workspaceId: invite.workspaceId })}
                        disabled={acceptInviteMutation.isPending}
                        className="rounded transition-colors disabled:opacity-50 flex items-center justify-center"
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: '#10B981',
                        }}
                        title="Aceitar convite"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13 4L6 11L3 8"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => rejectInviteMutation.mutate(invite.token)}
                        disabled={rejectInviteMutation.isPending}
                        className="rounded transition-colors disabled:opacity-50 flex items-center justify-center"
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: '#D9D9D9',
                        }}
                        title="Recusar convite"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4 4L12 12M12 4L4 12"
                            stroke="#FFFFFF"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                </div>
              </>
            ) : (
              <div 
                className="dark:text-[#CBD1CF]"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  color: '#A0AEC0',
                }}
              >
                Nenhum convite pendente
              </div>
            )}
          </div>

          {/* Container 2: Perfil */}
          <div 
            className="bg-white dark:bg-gray-800 p-6 relative"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <button
              onClick={() => {
                setProfileForm({
                  name: user?.name || currentUser?.name || '',
                  description: user?.description || '',
                  fullName: user?.fullName || '',
                  phone: user?.phone || '',
                  address: user?.address || '',
                });
                setAvatarPreview(user?.avatarUrl || null);
                setAvatarFile(null);
                setShowProfileEditModal(true);
              }}
              className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Editar perfil"
            >
              <svg width="18" height="18" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-900 dark:text-gray-100">
                <path d="M29.0207 14.9968L30.5223 16.4985C31.1559 17.1315 31.5261 17.9811 31.5585 18.8761C31.5908 19.7711 31.2828 20.6452 30.6966 21.3222L30.5237 21.5064L21.5081 30.5235C20.8749 31.1569 20.0252 31.5268 19.1302 31.5589C18.2352 31.591 17.3612 31.2828 16.6843 30.6963L16.5002 30.5235L14.9971 29.0218L29.0207 14.9968Z" fill="currentColor"/>
                <path opacity="0.3" d="M3.97817 3.97793C5.23192 2.72276 6.82567 2.09376 8.48884 2.27368C10.1336 2.45076 11.6409 3.39568 12.8182 4.88884C14.0762 6.48543 15.2265 8.75918 16.1275 10.802C16.5681 11.7993 17.7708 12.0402 18.5018 11.4749L18.6336 11.3602L19.5048 10.4889C20.138 9.85555 20.9877 9.48558 21.8827 9.45352C22.7777 9.42145 23.6517 9.72966 24.3286 10.3161L24.5128 10.4889L27.0174 12.9936L12.9924 27.0172L10.4892 24.5125C9.85579 23.8793 9.48583 23.0297 9.45376 22.1346C9.42169 21.2396 9.7299 20.3657 10.3163 19.6888L10.4892 19.5046L11.3604 18.6333C12.0688 17.925 11.8619 16.5948 10.8023 16.1273C8.75942 15.2263 6.48567 14.0759 4.88909 12.8179C3.39734 11.6407 2.45242 10.1333 2.27392 8.48859C2.094 6.82543 2.723 5.23026 3.97817 3.97793Z" fill="currentColor"/>
              </svg>
            </button>
            <h3 
              className="mb-4 text-gray-900 dark:text-[#CBD1CF]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              Perfil
            </h3>
            <div className="space-y-2">
              <div>
                <span 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  Nome completo:
                </span>
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                  }}
                >
                  {user?.fullName || currentUser?.fullName || '-'}
                </p>
              </div>
              <div>
                <span 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  Email:
                </span>
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                  }}
                >
                  {user?.email || currentUser?.email || '-'}
                </p>
              </div>
              <div>
                <span 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  Celular:
                </span>
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                  }}
                >
                  {user?.phone || '-'}
                </p>
              </div>
              <div>
                <span 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  Endereço:
                </span>
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                  }}
                >
                  {user?.address || '-'}
                </p>
              </div>
              <div>
                <span 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                    fontWeight: 500,
                  }}
                >
                  Descrição:
                </span>
                <p 
                  className="dark:text-[#CBD1CF]"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                  }}
                >
                  {user?.description || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Container 3: Em breve */}
          <div 
            className="bg-white dark:bg-gray-800 p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <h3 
              className="mb-4 text-gray-900 dark:text-[#CBD1CF]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              Em breve
            </h3>
            <div 
              className="flex items-center justify-center h-32 dark:text-[#CBD1CF]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#A0AEC0',
              }}
            >
              Em breve
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Create Modal — layout alinhado ao ConfirmDialog */}
      {showCreateModal && createPortal(
        <div
          className="workspaces-portal-ds fixed inset-0 bg-black/50 dark:bg-[var(--overlay)] flex items-center justify-center z-[10000] p-2 sm:p-4"
          onMouseDown={handleCreateModalBackdropMouseDown}
          onMouseUp={handleCreateModalBackdropMouseUp}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-workspace-modal-title"
        >
          <div
            className="bg-white dark:bg-[var(--bg-secondary)] w-full max-w-md max-h-[min(90vh,calc(100vh-1rem))] overflow-hidden flex flex-col shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-lg)]"
            style={{
              fontFamily: 'Poppins, sans-serif',
              borderRadius: 15,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between flex-shrink-0 p-5 pb-4 border-b border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]"
            >
              <h2
                id="create-workspace-modal-title"
                className="text-[16px] text-[#06141B] dark:text-[var(--text-primary)] pr-2"
                style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
              >
                Criar novo workspace
              </h2>
              <button
                type="button"
                onClick={closeCreateWorkspaceModal}
                className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80 flex-shrink-0 bg-[#06141B] text-[#F5F6F5] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:hover:bg-[var(--primary-hover)]"
                aria-label="Fechar"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form
              id="create-workspace-form"
              onSubmit={handleCreateSubmit}
              className="p-5 overflow-y-auto flex-1 min-h-0"
            >
              <div className="mb-5 flex flex-col items-center">
                <div className="relative mb-3">
                  {createImagePreview ? (
                    <img
                      src={createImagePreview}
                      alt="Preview"
                      className="w-20 h-20 rounded-full object-cover border-2 border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)]"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[#F3F4F6] dark:bg-[var(--bg-input)] flex items-center justify-center border-2 border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)]">
                      <span
                        className="text-[24px] font-normal text-[#718096] dark:text-[var(--text-tertiary)]"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                      >
                        +
                      </span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleCreateImageChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadImageMutation.isPending}
                  className="text-[12px] font-medium text-[#06141B] dark:text-[var(--primary)] hover:opacity-80 transition-opacity disabled:opacity-50"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {uploadImageMutation.isPending ? 'Enviando...' : 'Adicionar imagem'}
                </button>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="create-workspace-name"
                  className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Nome <span className="text-red-500 dark:text-[var(--error)]">*</span>
                </label>
                <input
                  id="create-workspace-name"
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  required
                  placeholder="Meu workspace"
                  className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)] placeholder:dark:text-[var(--text-tertiary)]"
                  style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                />
              </div>

              <div>
                <label
                  htmlFor="create-workspace-slug"
                  className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Slug <span className="text-red-500 dark:text-[var(--error)]">*</span>
                </label>
                <input
                  id="create-workspace-slug"
                  type="text"
                  value={createSlug}
                  onChange={(e) => setCreateSlug(e.target.value)}
                  required
                  placeholder="meu-workspace"
                  className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)] placeholder:dark:text-[var(--text-tertiary)]"
                  style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                />
                <p
                  className="mt-1 text-[12px] text-[#718096] dark:text-[var(--text-tertiary)]"
                  style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                >
                  Usado na URL (minúsculas, sem espaços)
                </p>
              </div>
            </form>

            <div className="px-5 py-4 flex justify-end gap-3 flex-shrink-0 border-t border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]">
              <button
                type="button"
                onClick={closeCreateWorkspaceModal}
                disabled={createMutation.isPending}
                className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#F9FAFB] text-[#2D3748] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-quaternary)]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="create-workspace-form"
                disabled={createMutation.isPending || uploadImageMutation.isPending}
                className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#06141B] text-[#F9FAFB] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:hover:bg-[var(--primary-hover)]"
                style={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {createMutation.isPending ? 'Aguarde...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal — ConfirmDialog + portal (z acima de sidebar/header) */}
      {deleteConfirmId &&
        createPortal(
          <div
            className="workspaces-portal-ds fixed inset-0 bg-black/50 dark:bg-[var(--overlay)] flex items-center justify-center z-[10000] p-2 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !deleteMutation.isPending) {
                setDeleteConfirmId(null);
              }
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="workspace-delete-dialog-title"
          >
            <div
              className="bg-white dark:bg-[var(--bg-secondary)] w-full max-w-md overflow-hidden flex flex-col shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-lg)]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                borderRadius: 15,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between flex-shrink-0 p-5 pb-4 border-b border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]">
                <h2
                  id="workspace-delete-dialog-title"
                  className="text-[16px] text-[#06141B] dark:text-[var(--text-primary)] pr-2"
                  style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
                >
                  Confirmar exclusão
                </h2>
                <button
                  type="button"
                  onClick={() => !deleteMutation.isPending && setDeleteConfirmId(null)}
                  disabled={deleteMutation.isPending}
                  className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80 flex-shrink-0 bg-[#06141B] text-[#F5F6F5] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:hover:bg-[var(--primary-hover)] disabled:opacity-50"
                  aria-label="Fechar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5">
                <p
                  className="text-[#06141B] dark:text-[var(--text-primary)]"
                  style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, lineHeight: 1.5 }}
                >
                  Tem certeza que deseja deletar este workspace? Esta ação não pode ser desfeita.
                </p>
              </div>

              <div className="px-5 py-4 flex justify-end gap-3 flex-shrink-0 border-t border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#F9FAFB] text-[#2D3748] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-quaternary)]"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#DC2626] text-white dark:bg-[var(--error)] dark:hover:opacity-90"
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {deleteMutation.isPending ? 'Aguarde...' : 'Deletar'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Plan Selection Modal */}
      <PlanSelectionModal
        isOpen={showPlanSelectionModal}
        onClose={() => setShowPlanSelectionModal(false)}
        onPlanSelected={(checkoutUrl) => {
          // Redirect to Stripe Checkout
          window.location.href = checkoutUrl;
        }}
        workspaceName={createName || 'Novo Workspace'}
      />

      {/* Profile Edit Modal — layout alinhado ao ConfirmDialog */}
      {showProfileEditModal &&
        createPortal(
          <div
            className="workspaces-portal-ds fixed inset-0 bg-black/50 dark:bg-[var(--overlay)] flex items-center justify-center z-[10000] p-2 sm:p-4"
            onClick={(e) => e.target === e.currentTarget && closeProfileEditModal()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-edit-modal-title"
          >
            <div
              className="bg-white dark:bg-[var(--bg-secondary)] w-full max-w-md max-h-[min(90vh,calc(100vh-1rem))] overflow-hidden flex flex-col shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[var(--shadow-lg)]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                borderRadius: 15,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between flex-shrink-0 p-5 pb-4 border-b border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]">
                <h2
                  id="profile-edit-modal-title"
                  className="text-[16px] text-[#06141B] dark:text-[var(--text-primary)] pr-2"
                  style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 400 }}
                >
                  Editar perfil
                </h2>
                <button
                  type="button"
                  onClick={closeProfileEditModal}
                  className="w-8 h-8 flex items-center justify-center rounded-[10px] transition-opacity hover:opacity-80 flex-shrink-0 bg-[#06141B] text-[#F5F6F5] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:hover:bg-[var(--primary-hover)]"
                  aria-label="Fechar"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form
                id="profile-edit-form"
                className="flex flex-col flex-1 min-h-0"
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (avatarFile) {
                      await uploadAvatarMutation.mutateAsync(avatarFile);
                    }

                    await updateProfileMutation.mutateAsync({
                      name: profileForm.name,
                      description: profileForm.description || null,
                      fullName: profileForm.fullName || null,
                      phone: profileForm.phone || null,
                      address: profileForm.address || null,
                    });
                    closeProfileEditModal();
                  } catch (error) {
                    console.error('Erro ao atualizar perfil:', error);
                  }
                }}
              >
                <div className="p-5 overflow-y-auto flex-1 min-h-0">
                  <p
                    className="text-[#06141B] dark:text-[var(--text-primary)] mb-5"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: 14, lineHeight: 1.5 }}
                  >
                    Atualize seus dados de exibição e contato. O e-mail não pode ser alterado aqui.
                  </p>

                  <div className="mb-5">
                    <label
                      className="block mb-2 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Imagem de perfil
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {avatarPreview || userProfile?.avatarUrl ? (
                          <img
                            src={
                              avatarPreview ||
                              getAssetUrl(userProfile?.avatarUrl) ||
                              userProfile?.avatarUrl ||
                              ''
                            }
                            alt="Avatar"
                            className="w-20 h-20 rounded-[10px] object-cover border-2 border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)]"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-[10px] bg-[#F3F4F6] dark:bg-[var(--bg-input)] flex items-center justify-center border-2 border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)]">
                            <span
                              className="text-[20px] font-semibold text-[#718096] dark:text-[var(--text-tertiary)]"
                              style={{ fontFamily: 'Poppins, sans-serif' }}
                            >
                              {userProfile?.name?.charAt(0).toUpperCase() ||
                                userProfile?.email?.charAt(0).toUpperCase() ||
                                '?'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex flex-wrap items-center gap-2">
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setAvatarFile(file);
                              const preview = URL.createObjectURL(file);
                              setAvatarPreview(preview);
                            }
                          }}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={uploadAvatarMutation.isPending}
                          className="text-[12px] font-medium text-[#06141B] dark:text-[var(--primary)] px-4 py-2 rounded-[10px] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] hover:opacity-90 transition-opacity disabled:opacity-50"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          Alterar foto
                        </button>
                        {avatarFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setAvatarFile(null);
                              setAvatarPreview(null);
                              if (avatarInputRef.current) {
                                avatarInputRef.current.value = '';
                              }
                            }}
                            className="text-[12px] font-medium px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 bg-[#F9FAFB] text-[#2D3748] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-quaternary)]"
                            style={{
                              fontFamily: 'Poppins, sans-serif',
                            }}
                          >
                            Remover seleção
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="profile-edit-name"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Nome <span className="text-red-500 dark:text-[var(--error)]">*</span>
                    </label>
                    <input
                      id="profile-edit-name"
                      type="text"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      required
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="profile-edit-fullname"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Nome completo
                    </label>
                    <input
                      id="profile-edit-fullname"
                      type="text"
                      value={profileForm.fullName}
                      onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="profile-edit-email"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      E-mail
                    </label>
                    <input
                      id="profile-edit-email"
                      type="email"
                      value={user?.email || currentUser?.email || ''}
                      disabled
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] cursor-not-allowed opacity-80 text-[#718096] dark:text-[var(--text-disabled)] bg-[#F3F4F6] dark:bg-[var(--bg-quaternary)] border border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="profile-edit-phone"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Celular
                    </label>
                    <input
                      id="profile-edit-phone"
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                  <div className="mb-4">
                    <label
                      htmlFor="profile-edit-address"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Endereço
                    </label>
                    <input
                      id="profile-edit-address"
                      type="text"
                      value={profileForm.address}
                      onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="profile-edit-description"
                      className="block mb-1 text-[12px] font-medium text-[#06141B] dark:text-[var(--text-primary)]"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      Descrição
                    </label>
                    <textarea
                      id="profile-edit-description"
                      value={profileForm.description}
                      onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                      rows={3}
                      className="w-full rounded-[10px] px-3 py-2 text-[14px] text-[#06141B] dark:text-[var(--text-primary)] bg-white dark:bg-[var(--bg-input)] border border-[rgba(6,20,27,0.12)] dark:border-[var(--border-primary)] outline-none focus:border-[#06141B] focus:ring-2 focus:ring-[#06141B]/15 dark:focus:border-[var(--border-focus)] dark:focus:ring-[rgba(0,217,255,0.22)] resize-y min-h-[5rem]"
                      style={{ fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 }}
                    />
                  </div>
                </div>

                <div className="px-5 py-4 flex justify-end gap-3 flex-shrink-0 border-t border-[rgba(6,20,27,0.08)] dark:border-[var(--border-primary)]">
                  <button
                    type="button"
                    onClick={closeProfileEditModal}
                    disabled={updateProfileMutation.isPending || uploadAvatarMutation.isPending}
                    className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#F9FAFB] text-[#2D3748] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-quaternary)]"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={updateProfileMutation.isPending || uploadAvatarMutation.isPending}
                    className="px-4 py-2 rounded-[10px] transition-opacity hover:opacity-90 disabled:opacity-50 bg-[#06141B] text-[#F9FAFB] dark:bg-[var(--primary)] dark:text-[var(--text-inverse)] dark:hover:bg-[var(--primary-hover)]"
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {updateProfileMutation.isPending || uploadAvatarMutation.isPending
                      ? 'Aguarde...'
                      : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
